/**
 * NotificationBell — fixed top-right bell icon with dropdown notification panel.
 *
 * New notifications surface via polling (services/notifications.subscribeToNotifications,
 * ~45s) through the Clerk-authed /api/notifications endpoint. Read state stored in
 * localStorage per-browser.
 *
 * Clicking a notification navigates to the relevant project via ctx.zoomToProject.
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { isEmbedded } from '../outline/state.js'
import {
  getNotifications,
  getReadIds,
  markRead,
} from '../services/notifications.js'

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function timeAgo(dateStr) {
  if (!dateStr) return 'recently'
  const diff = Date.now() - new Date(dateStr).getTime()
  if (isNaN(diff)) return 'recently'
  const mins = Math.floor(diff / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function entityLabel(type) {
  const map = {
    milestone:     'milestone',
    sheet:         'drawing sheet',
    bim:           'BIM system',
    coord:         'coordination item',
    agency_review: 'agency review',
    permit:        'permit',
    rfi:           'RFI',
    submittal:     'submittal',
    asi:           'ASI',
    contract:      'contract',
    thread:        'thread',
  }
  return map[type] || type || 'thread'
}

/** Project name is stored as a JSON-array string (e.g. '["Naini Residence"]'). */
function parseProjectName(pn) {
  if (!pn) return ''
  if (Array.isArray(pn)) return pn[0] || ''
  if (typeof pn === 'string') {
    if (pn.startsWith('[')) {
      try { const a = JSON.parse(pn); return Array.isArray(a) ? (a[0] || '') : pn } catch { return pn }
    }
    return pn
  }
  return ''
}

// Slack :shortcodes: → unicode for notification preview text. Mirrors EMOJI_MAP in SlackThreadPanel.
const PREVIEW_EMOJI = {
  '+1': '👍', 'thumbsup': '👍', '-1': '👎', 'thumbsdown': '👎',
  'white_check_mark': '✅', 'heavy_check_mark': '✔️', 'x': '❌',
  'eyes': '👀', 'heart': '❤️', 'fire': '🔥', '100': '💯',
  'raised_hands': '🙌', 'clap': '👏', 'pray': '🙏',
  'bangbang': '‼️', 'interrobang': '⁉️', 'exclamation': '❗', 'question': '❓',
  'arrow_right': '➡️', 'arrow_left': '⬅️', 'arrow_up': '⬆️', 'arrow_down': '⬇️',
  'slightly_smiling_face': '🙂', 'upside_down_face': '🙃', 'wink': '😉',
  'blush': '😊', 'smiley': '😃', 'smile': '😄', 'grinning': '😀', 'joy': '😂',
  'rofl': '🤣', 'laughing': '😆', 'sweat_smile': '😅',
  'sob': '😭', 'cry': '😢', 'scream': '😱', 'rage': '😡', 'angry': '😠',
  'thinking': '🤔', 'thinking_face': '🤔', 'sunglasses': '😎', 'nerd_face': '🤓',
  'skull': '💀', 'ghost': '👻', 'poop': '💩', 'hankey': '💩',
  'partying_face': '🥳', 'star_struck': '🤩', 'smiling_face_with_3_hearts': '🥰',
  'exploding_head': '🤯', 'zany_face': '🤪', 'monocle_face': '🧐',
  'shushing_face': '🤫', 'face_with_rolling_eyes': '🙄', 'triumph': '😤',
  'wave': '👋', 'muscle': '💪', 'ok_hand': '👌', 'crossed_fingers': '🤞',
  'v': '✌️', 'metal': '🤘', 'point_right': '👉', 'point_left': '👈',
  'tada': '🎉', 'rocket': '🚀', 'star': '⭐', 'sparkles': '✨',
  'warning': '⚠️', 'bulb': '💡', 'zap': '⚡', 'boom': '💥',
  'memo': '📝', 'calendar': '📅', 'pushpin': '📌', 'link': '🔗',
  'bell': '🔔', 'loud_sound': '🔊', 'mega': '📣',
  'trophy': '🏆', 'dart': '🎯', 'beer': '🍺', 'coffee': '☕',
}

/**
 * Convert Slack mrkdwn to readable plain text for a notification preview:
 * resolves <URL|label> links, channel mentions, broadcasts, and emoji; strips
 * user-mention IDs and bold/italic/strike/code markers. (No member map here, so
 * <@U…> becomes "@someone" unless Slack included a |name.)
 */
function slackToPlain(text) {
  if (!text) return ''
  let t = text
  t = t.replace(/<@[A-Z0-9]+\|([^>]+)>/g, '@$1')
  t = t.replace(/<@[A-Z0-9]+>/g, '@someone')
  t = t.replace(/<#[A-Z0-9]+\|([^>]+)>/g, '#$1')
  t = t.replace(/<!(here|channel|everyone)(?:\|[^>]*)?>/g, '@$1')
  t = t.replace(/<(https?:[^|>]+)\|([^>]+)>/g, '$2')
  t = t.replace(/<(https?:[^>]+)>/g, '$1')
  t = t.replace(/:([a-z0-9_+-]+):/gi, (m, name) => PREVIEW_EMOJI[name.toLowerCase()] ?? m)
  t = t.replace(/[*~`]/g, '')
  return t.replace(/\s+/g, ' ').trim()
}

/* Resizable-panel sizing. The user can drag the bottom handle to set how tall the list grows;
   the choice is saved per-browser. On open (and on window resize) it's clamped to what fits the
   current viewport — so a height set on a big screen "snaps back" to fit a smaller one without
   losing the saved preference. */
const NOTIF_H_KEY   = 'aj:notif:height'
const MIN_LIST_H    = 160
const DEFAULT_LIST_H = 380
function maxListHeight() {
  // Leave room for the bell offset, panel header, expander, and a bottom margin.
  return typeof window !== 'undefined' ? Math.max(MIN_LIST_H, window.innerHeight - 150) : DEFAULT_LIST_H
}
function clampListHeight(h) { return Math.max(MIN_LIST_H, Math.min(h, maxListHeight())) }

/* ── Component ───────────────────────────────────────────────────────────── */

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([])
  const [open,          setOpen]          = useState(false)
  const [readIds,       setReadIds]       = useState(() => getReadIds())
  const [showAll,       setShowAll]       = useState(false)
  const [listHeight,    setListHeight]    = useState(DEFAULT_LIST_H)
  const panelRef = useRef(null)

  const COLLAPSED_COUNT = 10

  /* On open: load the saved height and clamp it to the current viewport (snap-back). Re-clamp on
     window resize while open. The saved preference is left untouched so a bigger screen restores it. */
  useEffect(() => {
    if (!open) return
    let saved = DEFAULT_LIST_H
    try { const v = parseInt(localStorage.getItem(NOTIF_H_KEY) || '', 10); if (v) saved = v } catch { /* default */ }
    setListHeight(clampListHeight(saved))
    function onResize() { setListHeight(h => clampListHeight(h)) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [open])

  /* Drag the bottom handle to resize; persist the chosen height per-browser on release. */
  function startResize(e) {
    e.preventDefault()
    const startY = e.clientY
    const startH = listHeight
    let latest = startH
    function move(ev) { latest = clampListHeight(startH + (ev.clientY - startY)); setListHeight(latest) }
    function up() {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
      document.body.style.userSelect = ''
      try { localStorage.setItem(NOTIF_H_KEY, String(latest)) } catch { /* non-fatal */ }
    }
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }

  /* Load + poll every 45s. Now returns { notifications, readIds } from the
     server so read state is per-user across all devices. */
  useEffect(() => {
    let stopped = false
    async function load() {
      try {
        const { notifications: data, readIds: serverReadIds } = await getNotifications()
        if (!stopped) {
          setNotifications(data)
          setReadIds(serverReadIds)  // server-side read state (cross-device)
        }
      }
      catch { /* transient — retried on the next tick */ }
    }
    load()
    const timer = setInterval(load, 45000)
    return () => { stopped = true; clearInterval(timer) }
  }, [])

  /* Close dropdown on outside click */
  useEffect(() => {
    if (!open) return
    function onDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) { setOpen(false); setShowAll(false) }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const unreadCount = notifications.filter(n => !readIds.has(n.id)).length
  const visible     = showAll ? notifications : notifications.slice(0, COLLAPSED_COUNT)
  const hiddenCount = notifications.length - visible.length

  function handleToggle() {
    setOpen(v => {
      if (v) setShowAll(false)   // reset to collapsed when closing
      return !v
    })
  }

  function handleNotificationClick(n) {
    // Optimistically mark read in local state immediately, then persist server-side
    setReadIds(prev => new Set([...prev, n.id]))
    markRead([n.id])
    setOpen(false)
    // Open the Slack thread in context (over the current view) so the user can
    // jump straight back into the chat without losing their place.
    if (n.project_id && n.thread_ts) {
      window.dispatchEvent(new CustomEvent('open-slack-panel', { detail: {
        entityId:    n.entity_id,
        entityType:  n.entity_type,
        entityName:  n.entity_name || '',
        projectId:   n.project_id,
        projectName: parseProjectName(n.project_name),
        threadTs:    n.thread_ts,
      } }))
    } else if (n.project_id && ctx.zoomToProject) {
      // Fallback (older rows without thread_ts): navigate to the project
      if (ctx.closeAllWorkspaces) ctx.closeAllWorkspaces()
      ctx.zoomToProject(n.project_id)
    }
  }

  function handleMarkAllRead() {
    const ids = notifications.map(n => n.id)
    setReadIds(prev => new Set([...prev, ...ids]))
    markRead(ids)
  }

  // Mark a single notification read without opening it (stop the row's open-thread click).
  function handleMarkOneRead(e, id) {
    e.stopPropagation()
    setReadIds(prev => new Set([...prev, id]))
    markRead([id])
  }

  // A peek/split iframe (?split=1) is a second, full copy of the app loaded to show one
  // milestone/thread — same reasoning SiteNav and the outline's own breadcrumb chrome already
  // hide themselves for in embed mode. Without this the bell rendered a SECOND time inside the
  // iframe's own top-right corner (2026-09-04, Matt: "the modal shows the chat notification bell
  // in the upper right of the iframe... suppress that").
  if (isEmbedded()) return null

  return createPortal(
    // z-index 500 — matches SiteNav/the Slack thread panel's own tier (outline.css: full-bleed
    // workspaces like Billing/CRM sit at 400), not the outline-content tier this used to share
    // (300). At 300 it was a real bug, not just cosmetic: the bell was fully covered by ANY open
    // workspace's opaque background, so there was no way to reach Slack threads/notifications
    // from inside Billing, CRM, Tracker, Pulse, or Team at all — it needs to survive every one.
    <div ref={panelRef} style={{ position: 'fixed', top: 8, right: 12, zIndex: 500 }}>

      {/* ── Bell button ────────────────────────────────────────────────── */}
      <button
        onClick={handleToggle}
        title="Notifications"
        style={{
          position: 'relative',
          width: 30, height: 30,
          background: open ? 'var(--aj-border)' : 'var(--aj-bg-muted)',
          border: `0.5px solid ${open ? 'var(--aj-border-mid)' : 'var(--aj-border)'}`,
          borderRadius: 6,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(4px)',
          transition: 'background 0.12s, border-color 0.12s',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             style={{ stroke: unreadCount > 0 ? 'var(--aj-text)' : 'var(--aj-text-mid)' }}
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute', top: -5, right: -5,
            minWidth: 16, height: 16,
            background: '#c0392b', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: '"DM Mono",monospace', fontSize: 9, fontWeight: 600, color: '#fff',
            padding: '0 3px',
            border: '1.5px solid var(--aj-bg-page)',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </button>

      {/* ── Dropdown panel ─────────────────────────────────────────────── */}
      {open && (
        <div style={{
          position: 'absolute', top: 36, right: 0,
          width: 320,
          background: 'var(--aj-bg-elevated)',
          border: '0.5px solid var(--aj-border)',
          borderRadius: 8,
          boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          zIndex: 400,
        }}>

          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '0.5px solid var(--aj-border)',
          }}>
            <span style={{
              fontFamily: '"DM Sans",sans-serif', fontSize: 12,
              fontWeight: 600, color: 'var(--aj-text)',
            }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontFamily: '"DM Mono",monospace', fontSize: 9,
                  color: 'var(--aj-text-muted)', textDecoration: 'underline', textUnderlineOffset: 2,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: listHeight, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '36px 16px', textAlign: 'center',
                fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)',
              }}>
                No notifications yet
              </div>
            ) : (
              visible.map(n => {
                const isUnread  = !readIds.has(n.id)
                const preview   = slackToPlain(n.message_preview || '')
                const name      = n.entity_name || entityLabel(n.entity_type)
                const projName  = parseProjectName(n.project_name)
                const typeLabel = entityLabel(n.entity_type)

                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '10px 14px',
                      borderBottom: '0.5px solid var(--aj-border)',
                      cursor: 'pointer',
                      background: isUnread ? 'var(--aj-bg-subtle)' : 'transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-muted)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = isUnread ? 'var(--aj-bg-subtle)' : 'transparent' }}
                  >
                    {/* Unread dot / read spacer */}
                    <div style={{ width: 6, flexShrink: 0, marginTop: 5 }}>
                      {isUnread && (
                        <div style={{
                          width: 6, height: 6, borderRadius: '50%', background: 'var(--aj-text)',
                        }} />
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: '"DM Sans",sans-serif', fontSize: 11,
                        fontWeight: isUnread ? 600 : 400, color: 'var(--aj-text)',
                        marginBottom: 2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {n.sender_name || 'Someone'} replied to {name || 'a thread'}
                      </div>

                      {preview && (
                        <div style={{
                          fontFamily: '"DM Sans",sans-serif', fontSize: 11,
                          color: 'var(--aj-text-mid)', marginBottom: 4, lineHeight: 1.4,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {preview}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)',
                        }}>
                          {timeAgo(n.created_at)}
                        </span>
                        {typeLabel && (
                          <span style={{
                            fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)',
                            background: 'var(--aj-bg-muted)', borderRadius: 3, padding: '0 4px',
                          }}>
                            {typeLabel}
                          </span>
                        )}
                        {projName && (
                          <span style={{
                            fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                          }}>
                            · {projName}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Mark read without opening — only on unread rows */}
                    {isUnread && (
                      <button
                        onClick={e => handleMarkOneRead(e, n.id)}
                        title="Mark as read"
                        style={{
                          flexShrink: 0, marginTop: 1, padding: 2, borderRadius: 4,
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--aj-text-muted)', display: 'flex', alignItems: 'center',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#1D9E75'; e.currentTarget.style.background = 'rgba(29,158,117,0.1)' }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--aj-text-muted)'; e.currentTarget.style.background = 'none' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M3.5 8.5l3 3 6-6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                )
              })
            )}
          </div>

          {/* Expander — list keeps building; show 10, reveal the rest on demand */}
          {(hiddenCount > 0 || showAll) && notifications.length > COLLAPSED_COUNT && (
            <button
              onClick={() => setShowAll(v => !v)}
              style={{
                width: '100%', background: 'none', border: 'none',
                borderTop: '0.5px solid var(--aj-border)', cursor: 'pointer',
                padding: '9px 14px', fontFamily: '"DM Mono",monospace', fontSize: 10,
                color: 'var(--aj-text-muted)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
            >
              {showAll ? 'Show less' : `Show ${hiddenCount} more`}
            </button>
          )}

          {/* Resize handle — drag to set how tall the list grows (saved per-browser) */}
          <div
            onMouseDown={startResize}
            title="Drag to resize"
            style={{
              height: 11, cursor: 'ns-resize',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderTop: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)',
            }}
          >
            <div style={{ width: 26, height: 3, borderRadius: 2, background: 'var(--aj-border-mid)' }} />
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}
